import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { normalizeLang } from '@/../../shared/lang';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Sparkles, ArrowLeft, ArrowRight } from 'lucide-react';
import { useFeedbackById, useGenerateAnalysis, useEpicById } from '@/hooks/usePerformance';
import { ScorePill } from '@/components/performance/ScorePill';
import { PerformanceRadarChart } from '@/components/performance/PerformanceRadarChart';
import { toast } from 'sonner';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { MarkdownText } from '@/components/performance/MarkdownText';

export function PerformanceAnalysis() {
  const { feedbackId } = useParams<{ feedbackId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const backTarget = (location.state as { from?: string } | null)?.from ?? '/performance/history';
  const { t, i18n } = useTranslation();

  const { data: feedback, isLoading } = useFeedbackById(feedbackId!);
  const { data: epic } = useEpicById(feedback?.epicGlobalId ?? '');
  const generateAnalysis = useGenerateAnalysis();

  const analysis = feedback?.resultAnalysis;
  const epicType = feedback?.epicType ?? 'seasonal';
  const supportsAiAnalysis = epicType === 'performance' || epicType === 'feedback_session';

  const radarData = (feedback?.dimensions ?? [])
    .filter((d: any) => d.score !== null)
    .map((d: any) => {
      const epicDim = (epic?.dimensions ?? []).find((ed: any) => ed.id === d.id);
      return {
        subject: epicDim?.name ?? d.id,
        score: d.score ?? 0,
        fullMark: 10,
      };
    });

  async function handleGenerate() {
    try {
      await generateAnalysis.mutateAsync({ feedbackId: feedbackId!, language: normalizeLang(i18n.language) });
      toast.success(t('performance.analysis.generated_success'));
    } catch {
      toast.error(t('performance.analysis.generate_error'));
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <WinclapLoader size={48} className="text-foreground/25" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-4 sm:space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" aria-label={t('common.a11y.back')} onClick={() => navigate(backTarget)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{t('performance.analysis.title')}</h1>
            <p className="text-muted-foreground text-sm">
              {feedback?.clapperName} {feedback?.clapperLastName} · {feedback?.impactLevel}
            </p>
          </div>
        </div>
        <Button variant="outline" onClick={() => navigate('/performance/history')}>
          {t('performance.analysis.ver_historial')} <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>

      {/* Radar chart */}
      {radarData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('performance.analysis.radar')}</CardTitle>
          </CardHeader>
          <CardContent>
            <PerformanceRadarChart data={radarData} height={320} />
          </CardContent>
        </Card>
      )}

      {/* Scores list */}
      {feedback?.dimensions && feedback.dimensions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('performance.evaluation.competencies')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {(feedback.dimensions ?? []).map((dim: NonNullable<typeof feedback>['dimensions'][number]) => {
              const epicDim = (epic?.dimensions ?? []).find((ed: any) => ed.id === dim.id);
              return (
                <div key={dim.id} className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-sm">{epicDim?.name ?? dim.id}</span>
                      <ScorePill score={dim.score} />
                    </div>
                    {dim.justification && (
                      <MarkdownText className="text-xs text-muted-foreground">{dim.justification}</MarkdownText>
                    )}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* General comments */}
      {feedback?.generalComments && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { key: 'effective', label: t('performance.analysis.strengths') },
            { key: 'affective', label: t('performance.analysis.improvements') },
            { key: 'perspective', label: t('performance.analysis.action_plan') },
          ].map(({ key, label }) => (
            <Card key={key}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">{label}</CardTitle>
              </CardHeader>
              <CardContent>
                <MarkdownText className="text-sm text-muted-foreground">
                  {(feedback.generalComments as any)[key] || '—'}
                </MarkdownText>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* AI Analysis — only for performance/feedback_session types */}
      {supportsAiAnalysis && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">{t('performance.analysis.summary')}</CardTitle>
            {!analysis && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleGenerate}
                disabled={generateAnalysis.isPending}
              >
                {generateAnalysis.isPending ? (
                  <WinclapLoader size={16} className="mr-2" />
                ) : (
                  <Sparkles className="h-4 w-4 mr-2" />
                )}
                {generateAnalysis.isPending ? t('performance.analysis.generating') : t('performance.analysis.generate')}
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {analysis ? (
              <div className="space-y-4">
                {analysis.summary && (
                  <MarkdownText className="text-sm">{analysis.summary}</MarkdownText>
                )}
                {analysis.strengths?.length > 0 && (
                  <div>
                    <h4 className="font-medium text-sm mb-1">{t('performance.analysis.strengths')}</h4>
                    <ul className="list-disc pl-5 space-y-1">
                      {(analysis.strengths as string[]).map((s, i) => (
                        <li key={i} className="text-sm text-muted-foreground">
                          <MarkdownText className="[&_p]:my-0">{s}</MarkdownText>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {analysis.improvements?.length > 0 && (
                  <div>
                    <h4 className="font-medium text-sm mb-1">{t('performance.analysis.improvements')}</h4>
                    <ul className="list-disc pl-5 space-y-1">
                      {(analysis.improvements as string[]).map((s, i) => (
                        <li key={i} className="text-sm text-muted-foreground">
                          <MarkdownText className="[&_p]:my-0">{s}</MarkdownText>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                {t('performance.analysis.genera_un_analisis_con_ia_para')}
              </p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
